import type { Notification } from '@/types/notification'

// Narrow input shapes to ease testing without importing heavy external types
export type MessageLike = {
  id: string
  orderID: string
  toUserID: string
  type: string
  createdAt: string
  user: { id: string; name: string }
}

export type InvitationLike = {
  id: string
  userID: string
  type: string
  createdAt: string
}

export type ReviewMentionNotificationLike = {
  id: string
  reviewID: string
  href: string
  createdAt: string
  title?: string
  messageText?: string
  user: { id: string; name: string }
}

export type OrderLike = {
  id: string
  userID?: string
  requestType?: string | null
  title?: string | null
  name?: string | null
  createdAt: string
}

export function transformMessageToNotification(
  message: MessageLike,
): Notification {
  const isSellerView = ['stripe', 'reservation', 'punchOut'].includes(
    message.type,
  )
  const hrefBase = isSellerView ? '/orders/seller' : '/orders/request'

  return {
    id: message.id,
    key: `${message.orderID}-${message.toUserID}`,
    type: 'MESSAGE',
    userID: message.user.id,
    toUserID: message.toUserID,
    orderID: message.orderID,
    message: `Nouveau message de ${message.user.name}`,
    icon: 'ChatCenteredText',
    href: `${hrefBase}/message/${message.orderID}`,
    date: message.createdAt,
  }
}

export function transformReviewMentionToNotification(
  reviewMention: ReviewMentionNotificationLike,
): Notification {
  const title = reviewMention.title ? ` sur ${reviewMention.title}` : ''
  const message =
    reviewMention.messageText ??
    `Nouvelle mention de ${reviewMention.user.name}${title}`

  return {
    id: reviewMention.id,
    key: `${reviewMention.reviewID}-${reviewMention.id}`,
    type: 'REVIEW_MENTION',
    userID: reviewMention.user.id,
    message,
    icon: 'ChatCenteredText',
    href: reviewMention.href,
    date: reviewMention.createdAt,
  }
}

export function transformInvitationToNotification(
  invit: InvitationLike,
): Notification {
  const isProject = invit.type === 'PROJECT'
  return {
    id: invit.id,
    key: `${invit.id}-${invit.userID}`,
    type: invit.type,
    userID: invit.userID,
    message: isProject
      ? 'Vous êtes invité(e) à participer à un projet'
      : 'Vous êtes invité(e) à rejoindre une équipe',
    icon: isProject ? 'Briefcase' : 'UsersThree',
    href: isProject ? '/projects' : '/team',
    date: invit.createdAt,
  }
}

export function transformOrderToNotification(order: OrderLike): Notification {
  const isSellerView = ['stripe', 'reservation', 'punchOut'].includes(
    order.requestType ?? '',
  )
  const hrefBase = isSellerView ? '/orders/seller' : '/orders/request'

  return {
    id: order.id,
    key: `${order.id}-${order.userID ?? ''}`,
    type: 'ORDER',
    userID: order.userID,
    message:
      `Nouvelle ${order.title ?? 'commande'} de ${order.name ?? ''}`.trim(),
    icon: 'SealPercent',
    href: `${hrefBase}/details/${order.id}`,
    date: order.createdAt,
  }
}
